import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Plus, Sparkles, Wand2, AlertTriangle, Trash2, X, Upload, Image as ImageIcon } from 'lucide-react';
import api from '../../lib/api';
import { WhatsAppTemplatePreview } from './WhatsAppTemplatePreview';
import { WhatsAppMediaLibrary } from './WhatsAppMediaLibrary';

const FIELD_OPTIONS = [
  { value: 'first_name', label: 'First name' },
  { value: 'full_name', label: 'Full name' },
  { value: 'company', label: 'Company' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'city', label: 'City' },
  { value: 'title', label: 'Job title' },
  { value: 'country', label: 'Country' },
  { value: 'custom', label: 'Custom text' },
];

const METER = {
  green: { cls: 'bg-emerald-500', text: 'text-emerald-600', label: 'Looks good — likely to be approved' },
  amber: { cls: 'bg-amber-500', text: 'text-amber-600', label: 'Risky — review the warnings' },
  red: { cls: 'bg-red-500', text: 'text-red-600', label: 'Will be rejected — fix before submitting' },
};

const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9_ ]/g, '').replace(/\s+/g, '_').slice(0, 512);
const countVars = (text) => {
  const m = (text || '').match(/\{\{\s*(\d+)\s*\}\}/g) || [];
  const idxs = m.map((x) => parseInt(x.replace(/[^0-9]/g, ''), 10)).filter((n) => !Number.isNaN(n));
  return idxs.length ? Math.max(...idxs) : 0;
};

const BUTTON_DEFAULTS = {
  QUICK_REPLY: { type: 'QUICK_REPLY', text: '' },
  URL: { type: 'URL', text: '', url: '', example: '' },
  PHONE_NUMBER: { type: 'PHONE_NUMBER', text: '', phone_number: '' },
  COPY_CODE: { type: 'COPY_CODE', text: 'Copy code', example: '' },
};

const emptyForm = { name: '', category: 'MARKETING', language: 'en_US', body_text: '', footer_text: '' };

/**
 * WhatsApp template builder — full card (text header + body + footer + buttons),
 * AI drafting, live linter meter, and a live phone preview. Extracted from the
 * panel so the growing builder stays isolated.
 */
export const WhatsAppTemplateBuilder = ({ onCreated, onCancel, initial, existingNames }) => {
  const [form, setForm] = useState(initial?.form || emptyForm);
  const [header, setHeader] = useState(initial?.header || { format: 'NONE', text: '', sample: '' });
  const [buttons, setButtons] = useState(initial?.buttons || []);
  const [tplVars, setTplVars] = useState(initial?.tplVars || []);
  const [creating, setCreating] = useState(false);
  const editId = initial?.editId || null;
  const [allowance, setAllowance] = useState(null);

  const [lint, setLint] = useState(null);
  const [aiIdea, setAiIdea] = useState('');
  const [aiVariants, setAiVariants] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);

  const bodyVarCount = countVars(form.body_text);
  // Name-collision precheck (skip when editing — name is fixed).
  const nameTaken = !editId && form.name && existingNames instanceof Set && existingNames.has(form.name.toLowerCase());

  // Fetch remaining edits when opening in edit mode.
  useEffect(() => {
    if (!editId || !initial?.form?.name) return;
    api.get(`/api/social-connectors/whatsapp/templates/${encodeURIComponent(initial.form.name)}/edit-allowance`)
      .then((r) => setAllowance(r.data)).catch(() => {});
  }, [editId, initial]);

  // Header shape sent to the API — never leak the local blob previewUrl.
  const headerForApi = () => {
    if (header.format === 'NONE') return null;
    const { previewUrl, ...rest } = header;
    return rest;
  };

  const uploadMedia = async (fileObj) => {
    if (!fileObj) return;
    setUploadingMedia(true);
    const fd = new FormData();
    fd.append('file', fileObj);
    try {
      const r = await api.post('/api/social-connectors/whatsapp/templates/upload-media', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = r.data || {};
      if (!d.header_handle) throw new Error('no handle');
      let previewUrl = '';
      try { previewUrl = URL.createObjectURL(fileObj); } catch { /* ignore */ }
      setHeader((h) => ({
        format: d.format || h.format, header_handle: d.header_handle,
        storage_key: d.storage_key, filename: d.filename, mime: d.mime,
        width: d.width, height: d.height,
        previewUrl,
        text: '', sample: '',
      }));
      toast.success('Media uploaded — ready for review');
    } catch (e) {
      toast.error('Upload failed', { description: e?.response?.data?.detail || 'Could not upload the media' });
    } finally {
      setUploadingMedia(false);
    }
  };

  // Keep per-variable sample rows in sync with {{n}} count.
  useEffect(() => {
    setTplVars((prev) => {
      const next = [];
      for (let i = 0; i < bodyVarCount; i++) {
        next.push(prev[i] || { field: i === 0 ? 'first_name' : i === 1 ? 'company' : 'custom', sample: '' });
      }
      return next;
    });
  }, [bodyVarCount]);

  // Debounced live lint (body + footer + header + buttons) against Meta's rules.
  useEffect(() => {
    if (!form.body_text.trim()) { setLint(null); return undefined; }
    const t = setTimeout(() => {
      const examples = tplVars.map((v) => v.sample).filter(Boolean);
      api.post('/api/social-connectors/whatsapp/templates/lint', {
        body_text: form.body_text, category: form.category, examples,
        footer_text: form.footer_text,
        header: headerForApi(),
        buttons,
      }).then((r) => setLint(r.data)).catch(() => setLint(null));
    }, 450);
    return () => clearTimeout(t);
  }, [form.body_text, form.footer_text, form.category, tplVars, header, buttons]);

  const runAiDraft = () => {
    if (!aiIdea.trim()) { toast.error('Describe the message you want'); return; }
    setAiLoading(true);
    api.post('/api/social-connectors/whatsapp/templates/ai-draft', { idea: aiIdea, category: form.category })
      .then((r) => { setAiVariants(r.data?.variants || []); if (!(r.data?.variants || []).length) toast.error('No drafts came back'); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'AI draft failed'))
      .finally(() => setAiLoading(false));
  };

  const useVariant = (v) => {
    setForm((f) => ({ ...f, body_text: v.body || '', footer_text: v.footer || f.footer_text, name: slugify(v.name || f.name) }));
    if ((v.header_text || '').trim()) setHeader({ format: 'TEXT', text: v.header_text, sample: v.header_sample || '' });
    if ((v.buttons || []).length) setButtons(v.buttons.map((b) => ({ ...BUTTON_DEFAULTS[(b.type || '').toUpperCase()], ...b })));
    if ((v.example_values || []).length) {
      setTplVars((prev) => prev.map((row, i) => ({ ...row, sample: v.example_values[i] || row.sample })));
    }
    setAiVariants([]);
    toast.success('Draft loaded — review and submit');
  };

  const fixWithAi = () => {
    setFixing(true);
    api.post('/api/social-connectors/whatsapp/templates/ai-fix', {
      body_text: form.body_text, category: form.category, footer_text: form.footer_text,
      header: header.format === 'TEXT' ? header : null, buttons,
      reason: initial?.rejectedReason || '',
    }).then((r) => {
      setForm((f) => ({ ...f, body_text: r.data?.body || f.body_text, footer_text: r.data?.footer || f.footer_text }));
      if (r.data?.header) setHeader({ format: 'TEXT', text: r.data.header.text || '', sample: r.data.header.sample || '' });
      if ((r.data?.buttons || []).length) setButtons(r.data.buttons.map((b) => ({ ...BUTTON_DEFAULTS[(b.type || '').toUpperCase()], ...b })));
      if ((r.data?.example_values || []).length) setTplVars((prev) => prev.map((row, i) => ({ ...row, sample: r.data.example_values[i] || row.sample })));
      toast.success('Fixed — re-checking');
    }).catch((e) => toast.error(e?.response?.data?.detail || 'Fix failed'))
      .finally(() => setFixing(false));
  };

  const addButton = (type) => {
    setButtons((prev) => [...prev, { ...BUTTON_DEFAULTS[type] }]);
  };
  const updateButton = (i, patch) => setButtons((prev) => { const n = [...prev]; n[i] = { ...n[i], ...patch }; return n; });
  const removeButton = (i) => setButtons((prev) => prev.filter((_, idx) => idx !== i));

  const create = useCallback(async () => {
    const name = slugify(form.name);
    if (!name || !form.body_text.trim()) { toast.error('Name and body are required'); return; }
    if (bodyVarCount > 0 && tplVars.some((v) => !(v.sample || '').trim())) {
      toast.error('Add a sample value for every {{variable}} (Meta requires one to approve)');
      return;
    }
    setCreating(true);
    try {
      const examples = tplVars.map((v) => (v.sample || '').trim());
      const variable_map = tplVars.map((v) => (v.field === 'custom'
        ? { field: 'custom', value: (v.sample || '').trim() }
        : { field: v.field }));
      if (editId) {
        await api.post(`/api/social-connectors/whatsapp/templates/${encodeURIComponent(editId)}/edit`, {
          name, category: form.category, body_text: form.body_text, footer_text: form.footer_text,
          examples, variable_map, header: headerForApi(), buttons,
        });
        toast.success('Changes saved — back to review');
      } else {
        await api.post('/api/social-connectors/whatsapp/templates', {
          ...form, name, examples, variable_map,
          header: headerForApi(),
          buttons,
        });
        toast.success('Template submitted for review');
      }
      onCreated?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(editId ? 'Save failed' : 'Create failed', { description: typeof detail === 'string' ? detail : 'Could not save template' });
    } finally {
      setCreating(false);
    }
  }, [form, header, buttons, tplVars, bodyVarCount, onCreated, editId]);

  const previewSamples = tplVars.map((v) => v.sample);

  return (
    <div className="space-y-3 p-3 rounded-lg border border-border bg-muted/20" data-testid="wa-template-form">
      {editId && (
        <div className="rounded-md border border-sky-500/30 bg-sky-500/5 p-2 text-[11px] text-sky-700" data-testid="wa-builder-edit-banner">
          Editing “{initial?.form?.name}” — saving sends it back to Meta review. Name &amp; language can't change.
          {allowance && <span className="ml-1 font-medium">Edits left: {allowance.remaining_24h}/1 today · {allowance.remaining_30d}/10 this month.</span>}
        </div>
      )}
      {initial?.rejectedReason && (
        <div className="rounded-md border border-red-500/30 bg-red-500/5 p-2 text-[11px] text-red-600" data-testid="wa-builder-rejected-banner">
          <AlertTriangle className="mr-1 inline h-3 w-3" />Meta rejected this: {initial.rejectedReason}
        </div>
      )}

      {/* AI drafting */}
      <div className="rounded-lg border border-primary/25 bg-primary/[0.04] p-2.5">
        <label className="flex items-center gap-1.5 text-xs font-semibold text-primary">
          <Sparkles className="h-3.5 w-3.5" /> Draft a full card with AI
        </label>
        <div className="mt-1.5 flex items-center gap-2">
          <Input placeholder="e.g. remind trial ends in 3 days, invite to a demo, add a Book button" value={aiIdea}
            onChange={(e) => setAiIdea(e.target.value)} className="h-8 text-xs" data-testid="wa-ai-idea" />
          <Button size="sm" className="h-8 gap-1.5 shrink-0" onClick={runAiDraft} disabled={aiLoading} data-testid="wa-ai-draft-btn">
            {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}Draft
          </Button>
        </div>
        {aiLoading && <p className="mt-1 text-[10px] text-muted-foreground" data-testid="wa-ai-loading-hint">Generating 3 approval-ready cards…</p>}
        {aiVariants.length > 0 && (
          <div className="mt-2 space-y-2" data-testid="wa-ai-variants">
            {aiVariants.map((v, i) => (
              <div key={i} className="rounded-md border border-border bg-background p-2" data-testid={`wa-ai-variant-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[10px] font-semibold ${METER[v.lint?.level]?.text || ''}`}>● {v.lint?.level} · {v.lint?.score}</span>
                  <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => useVariant(v)} data-testid={`wa-ai-use-${i}`}>Use this</Button>
                </div>
                {v.header_text && <p className="mt-1 text-[11px] font-bold">{v.header_text}</p>}
                <p className="mt-0.5 text-xs leading-relaxed">{v.body}</p>
                {v.footer && <p className="mt-0.5 text-[10px] text-muted-foreground">{v.footer}</p>}
                {(v.buttons || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {v.buttons.map((b, bi) => (<span key={bi} className="rounded bg-sky-500/10 px-1.5 py-0.5 text-[10px] text-sky-600">{b.text || b.type}</span>))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {/* LEFT — form */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium">Name</label>
              <Input placeholder="welcome_message" value={form.name} disabled={!!editId}
                onChange={(e) => setForm((f) => ({ ...f, name: slugify(e.target.value) }))} data-testid="wa-template-name" />
              {nameTaken && <p className="text-[10px] text-red-600" data-testid="wa-name-taken">A template named "{form.name}" already exists — pick another name.</p>}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="wa-template-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MARKETING">Marketing</SelectItem>
                  <SelectItem value="UTILITY">Utility</SelectItem>
                  <SelectItem value="AUTHENTICATION">Authentication</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Language</label>
            <Select value={form.language} onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}>
              <SelectTrigger data-testid="wa-template-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en_US">English (US)</SelectItem>
                <SelectItem value="en_GB">English (UK)</SelectItem>
                <SelectItem value="es_ES">Spanish</SelectItem>
                <SelectItem value="pt_BR">Portuguese (BR)</SelectItem>
                <SelectItem value="hi">Hindi</SelectItem>
                <SelectItem value="ar">Arabic</SelectItem>
                <SelectItem value="fr">French</SelectItem>
                <SelectItem value="de">German</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Header */}
          <div className="space-y-1">
            <label className="text-xs font-medium">Header (optional)</label>
            <Select value={header.format} onValueChange={(v) => setHeader((h) => ({ format: v, text: h.text || '', sample: h.sample || '' }))}>
              <SelectTrigger className="h-8 text-xs" data-testid="wa-template-header-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">None</SelectItem>
                <SelectItem value="TEXT">Text</SelectItem>
                <SelectItem value="IMAGE">Image (JPG/PNG)</SelectItem>
                <SelectItem value="VIDEO">Video (MP4)</SelectItem>
                <SelectItem value="DOCUMENT">Document (PDF)</SelectItem>
              </SelectContent>
            </Select>
            {header.format === 'TEXT' && (
              <div className="space-y-1">
                <Input maxLength={60} placeholder="e.g. Your order update" value={header.text}
                  onChange={(e) => setHeader((h) => ({ ...h, text: e.target.value }))} className="h-8 text-xs" data-testid="wa-template-header-text" />
                {/\{\{\s*1\s*\}\}/.test(header.text) && (
                  <Input placeholder="Sample for {{1}}" value={header.sample}
                    onChange={(e) => setHeader((h) => ({ ...h, sample: e.target.value }))} className="h-8 text-xs" data-testid="wa-template-header-sample" />
                )}
              </div>
            )}
            {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format) && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-end">
                  <Button size="sm" variant="ghost" className="h-6 gap-1 text-[11px]" onClick={() => setShowLibrary((s) => !s)} data-testid="wa-choose-library-btn">
                    <ImageIcon className="h-3 w-3" />{showLibrary ? 'Hide library' : 'Choose from library'}
                  </Button>
                </div>
                {showLibrary && (
                  <WhatsAppMediaLibrary
                    format={header.format}
                    onClose={() => setShowLibrary(false)}
                    onSelect={(h) => { setHeader({ ...h, text: '', sample: '' }); setShowLibrary(false); toast.success('Media selected from library'); }}
                  />
                )}
                <div
                  className={`rounded-md border border-dashed p-3 text-center text-xs ${(header.header_handle || header.storage_key) ? 'border-emerald-400 bg-emerald-500/5' : 'border-border bg-background'}`}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) uploadMedia(e.dataTransfer.files[0]); }}
                  data-testid="wa-header-dropzone"
                >
                  <input id="wa-header-file" type="file" className="hidden"
                    accept={header.format === 'IMAGE' ? 'image/jpeg,image/png' : header.format === 'VIDEO' ? 'video/mp4' : 'application/pdf'}
                    onChange={(e) => e.target.files?.[0] && uploadMedia(e.target.files[0])} data-testid="wa-header-file-input" />
                  {uploadingMedia ? (
                    <span className="flex items-center justify-center gap-1.5 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />Uploading…</span>
                  ) : (header.header_handle || header.storage_key) ? (
                    <div className="flex items-center justify-center gap-2">
                      <span className="truncate text-emerald-600" data-testid="wa-header-uploaded">✓ {header.filename || 'Uploaded'}{header.width && header.height ? ` · ${header.width}×${header.height}` : ''}</span>
                      <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => document.getElementById('wa-header-file').click()} data-testid="wa-header-replace">Replace</Button>
                    </div>
                  ) : (
                    <button type="button" className="w-full text-muted-foreground hover:text-foreground" onClick={() => document.getElementById('wa-header-file').click()} data-testid="wa-header-upload-btn">
                      <Upload className="mx-auto mb-1 h-4 w-4" />
                      Drag &amp; drop or click to upload {header.format === 'IMAGE' ? 'a JPG/PNG (≤5MB)' : header.format === 'VIDEO' ? 'an MP4 (≤16MB)' : 'a PDF (≤100MB)'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Body */}
          <div className="space-y-1">
            <label className="flex items-center justify-between text-xs font-medium">
              <span>Body</span>
              <span className={`text-[10px] ${form.body_text.length > 1024 ? 'text-red-600' : 'text-muted-foreground'}`} data-testid="wa-body-counter">{form.body_text.length}/1024</span>
            </label>
            <Textarea rows={3} placeholder="Hi {{1}}, thanks for connecting with {{2}}!" value={form.body_text}
              onChange={(e) => setForm((f) => ({ ...f, body_text: e.target.value }))} data-testid="wa-template-body" />
            <p className="text-[10px] text-muted-foreground">Use <span className="font-mono">{'{{1}}'}</span>, <span className="font-mono">{'{{2}}'}</span>… for variables that fill from each lead.</p>
          </div>

          {/* Variables */}
          {bodyVarCount > 0 && (
            <div className="space-y-2 rounded-md border border-border bg-background p-2" data-testid="wa-template-vars">
              <p className="text-[11px] font-medium">Variables — sample + auto-fill field <span className="text-muted-foreground">(Meta needs a sample)</span></p>
              {tplVars.map((v, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`wa-template-var-${i + 1}`}>
                  <span className="text-[10px] font-mono text-muted-foreground w-8 shrink-0">{`{{${i + 1}}}`}</span>
                  <Select value={v.field} onValueChange={(val) => setTplVars((p) => { const n = [...p]; n[i] = { ...n[i], field: val }; return n; })}>
                    <SelectTrigger className="h-8 text-xs w-32 shrink-0" data-testid={`wa-template-var-field-${i + 1}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{FIELD_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                  </Select>
                  <Input className="h-8 text-xs flex-1" placeholder={v.field === 'custom' ? 'Fixed text to send' : 'Sample, e.g. John'}
                    value={v.sample || ''} onChange={(e) => setTplVars((p) => { const n = [...p]; n[i] = { ...n[i], sample: e.target.value }; return n; })}
                    data-testid={`wa-template-var-sample-${i + 1}`} />
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="space-y-1">
            <label className="text-xs font-medium">Footer (optional)</label>
            <Input maxLength={60} placeholder="Reply STOP to unsubscribe" value={form.footer_text}
              onChange={(e) => setForm((f) => ({ ...f, footer_text: e.target.value }))} data-testid="wa-template-footer" />
          </div>

          {/* Buttons */}
          <div className="space-y-2 rounded-md border border-border bg-background p-2" data-testid="wa-template-buttons">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-medium">Buttons (optional)</p>
              <Select value="" onValueChange={(v) => v && addButton(v)}>
                <SelectTrigger className="h-7 w-32 text-[11px]" data-testid="wa-add-button"><span className="flex items-center gap-1"><Plus className="h-3 w-3" />Add button</span></SelectTrigger>
                <SelectContent>
                  <SelectItem value="QUICK_REPLY">Quick reply</SelectItem>
                  <SelectItem value="URL">Visit website (URL)</SelectItem>
                  <SelectItem value="PHONE_NUMBER">Call phone number</SelectItem>
                  <SelectItem value="COPY_CODE">Copy offer code</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {buttons.length === 0 && <p className="text-[10px] text-muted-foreground">Add quick replies or call-to-action buttons (up to 10).</p>}
            {buttons.map((b, i) => (
              <div key={i} className="space-y-1 rounded border border-border/60 p-1.5" data-testid={`wa-button-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-muted-foreground">{b.type.replace('_', ' ')}</span>
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-500" onClick={() => removeButton(i)} data-testid={`wa-button-remove-${i}`}><X className="h-3 w-3" /></Button>
                </div>
                {b.type !== 'COPY_CODE' && (
                  <Input className="h-7 text-xs" maxLength={25} placeholder="Button text" value={b.text || ''}
                    onChange={(e) => updateButton(i, { text: e.target.value })} data-testid={`wa-button-text-${i}`} />
                )}
                {b.type === 'URL' && (
                  <>
                    <Input className="h-7 text-xs" placeholder="https://yourdomain.com/{{1}} (optional variable)" value={b.url || ''}
                      onChange={(e) => updateButton(i, { url: e.target.value })} data-testid={`wa-button-url-${i}`} />
                    {/\{\{/.test(b.url || '') && (
                      <Input className="h-7 text-xs" placeholder="Full sample link" value={b.example || ''}
                        onChange={(e) => updateButton(i, { example: e.target.value })} data-testid={`wa-button-url-example-${i}`} />
                    )}
                  </>
                )}
                {b.type === 'PHONE_NUMBER' && (
                  <Input className="h-7 text-xs" placeholder="+14155551234" value={b.phone_number || ''}
                    onChange={(e) => updateButton(i, { phone_number: e.target.value })} data-testid={`wa-button-phone-${i}`} />
                )}
                {b.type === 'COPY_CODE' && (
                  <Input className="h-7 text-xs" maxLength={15} placeholder="Coupon code, e.g. SAVE20" value={b.example || ''}
                    onChange={(e) => updateButton(i, { example: e.target.value })} data-testid={`wa-button-code-${i}`} />
                )}
              </div>
            ))}
          </div>

          {/* Lint meter */}
          {lint && (
            <div className="rounded-md border border-border bg-background p-2" data-testid="wa-lint-meter">
              <div className="flex items-center justify-between">
                <span className={`text-[11px] font-semibold ${METER[lint.level]?.text}`} data-testid="wa-lint-level">{METER[lint.level]?.label}</span>
                <span className="text-[10px] text-muted-foreground">{lint.score}/100</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div className={`h-full ${METER[lint.level]?.cls}`} style={{ width: `${lint.score}%` }} />
              </div>
              {(lint.errors?.length > 0 || lint.warnings?.length > 0) && (
                <ul className="mt-1.5 space-y-0.5">
                  {lint.errors?.map((e, i) => (
                    <li key={`e${i}`} className="flex items-start gap-1 text-[10px] text-red-600" data-testid={`wa-lint-error-${i}`}><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{e}</li>
                  ))}
                  {lint.warnings?.map((w, i) => (<li key={`w${i}`} className="text-[10px] text-amber-600">{w}</li>))}
                </ul>
              )}
              {lint.level !== 'green' && (
                <Button size="sm" variant="outline" className="mt-2 h-7 gap-1.5 text-[11px]" onClick={fixWithAi} disabled={fixing} data-testid="wa-fix-ai-btn">
                  {fixing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}Fix with AI
                </Button>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — live preview */}
        <div className="lg:sticky lg:top-2 self-start">
          <WhatsAppTemplatePreview header={header} body={form.body_text} footer={form.footer_text} buttons={buttons} samples={previewSamples} />
        </div>
      </div>

      <div className="flex gap-2">
        <Button className="flex-1" onClick={create} disabled={creating || lint?.level === 'red' || nameTaken || (editId && allowance && !allowance.can_edit)} data-testid="wa-template-submit">
          {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
          {lint?.level === 'red' ? 'Fix the errors above to submit' : editId ? 'Save changes (re-submit)' : 'Submit for Approval'}
        </Button>
        {onCancel && <Button variant="ghost" onClick={onCancel} data-testid="wa-template-cancel">Cancel</Button>}
      </div>
    </div>
  );
};

export default WhatsAppTemplateBuilder;
